import React, {useContext, useEffect, useState} from 'react';
import NotificationForm from '../../views/notification/notification-form';
import NotificationTable from '../../views/notification/notification-table';
import {inject, observer} from 'mobx-react';
import i18n from '../../stores/translate-store';
import {useIntl} from 'react-intl';
import useAlerts from '../../hooks/use-alerts';
import {ModalContext} from '../../apiContexts';
import useServices from '../../hooks/use-services';
import useNotificationType from '../../hooks/use-notification-type';
import useAlarmType from '../../hooks/use-alarm-types';
import useNotificatiorType from '../../hooks/use-notificatior-type';
import useUsers from '../../hooks/use-users';
import useTimezone from '../../hooks/use-timezone';
import {formatDateTimeColumn} from '../../helpers/excel-helper';

const noCreator = {getUserById: () => undefined};

// MON-505: formatNotifications decora o item da lista com creatorName/creatorEmail so para
// exibicao. O merge de update ({...notifications[index], ...data}) reenvia isso pra API, que nao
// conhece esses campos (org.traccar.model.Notification) e devolve 400 Unrecognized field.
export const cleanupForUpdate = (notification) => {
    const clean = {...notification};
    delete clean.title;
    delete clean.alarms;
    delete clean.geofences;
    delete clean.creatorName;
    delete clean.creatorEmail;
    delete clean.createdAtFormatted;
    delete clean.updatedAtFormatted;
    return clean;
};

export const formatNotifications = (arr, intl, userStore = noCreator, timezone) => {
    return arr.map(({id, always, attributes, notificators, type, calendarId, userId, createdAt, updatedAt}) => {
        const creator = userStore.getUserById(userId);
        return {
            id,
            always,
            attributes,
            notificators,
            userId,
            creatorName: creator ? creator.name : '',
            creatorEmail: creator ? creator.email : '',
            createdAt,
            updatedAt,
            createdAtFormatted: formatDateTimeColumn(createdAt, timezone),
            updatedAtFormatted: formatDateTimeColumn(updatedAt, timezone),
            title: intl.formatMessage({id: 'event' + type.charAt(0).toUpperCase() + type.slice(1)}),
            type,
            calendarId,
            alarms: attributes ? attributes['alarms'] : undefined,
            // Geofence ids are persisted as a CSV string but geofence options carry numeric ids;
            // ReactSelect matches selected values against options by strict equality, so these
            // must be numbers or a saved selection never shows as selected when the form reopens.
            geofences: attributes && attributes['geofences'] ?
                attributes['geofences'].split(',').map(Number) : undefined,
        };
    });
};

const NotificationContainer = (props) => {
    const intl = useIntl();
    const {toast} = useAlerts();
    const {types: allNotificationTypes} = useNotificationType();
    const {alarmTypes} = useAlarmType();
    const {allNotificator} = useNotificatiorType();
    const {calendarService, geofenceService, notificationService} = useServices();
    const {showModalLoading, isModalLoading} = useContext(ModalContext);
    const {users: userStore} = useUsers();
    const {timezone} = useTimezone();
    const [view, setView] = useState('notificationList');
    const [notifications, setNotifications] = useState([]);
    const [calendars, setCalendars] = useState([]);
    const [geofences, setGeofences] = useState([]);
    const [data, setData] = useState({});

    const formatter = (arr) => formatNotifications(arr, intl, userStore, timezone);

    useEffect(() => {
        (async () => {
            const [response, calendars, geofences] = await Promise.all([
                notificationService.list(), calendarService.list(), geofenceService.list(),
            ]);

            setNotifications(formatter(response));

            const items = [];
            calendars.map((calendar) => {
                items.push({id: calendar.id, title: calendar.name});
            });
            setCalendars(items);

            setGeofences(geofences.map((geofence) => ({id: geofence.id, title: geofence.name})));
        })();
    }, []);


    const notificationForm = (data) => {
        setData(Object.keys(data).length ? data : {});
        setView('notificationForm');
    };

    const newRegister = async (data) => {
        try {
            showModalLoading(true);
            const result = await notificationService.save(data);
            setNotifications(formatter([...notifications, result]));
            setView('notificationList');
            toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const updateRegister = async (data, afterTo = 'notificationList') => {
        try {
            showModalLoading(true);
            const index = notifications.findIndex(({id}) => id === parseInt(data.id));
            const notification = cleanupForUpdate({...notifications[index], ...data});

            const result = await notificationService.update(notification);

            notifications.splice(index, 1, result);
            setNotifications(formatter([...notifications]));

            setView(afterTo);
            toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const removeRegister = async (data) => {
        try {
            showModalLoading(true);
            await notificationService.remove(data.id);
            setNotifications(notifications.filter(({id}) => id !== data.id));
            setView('notificationList');
            toast.success(intl.formatMessage({id: 'sharedRemovedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };


    return (
        <>
            {
                {
                    notificationList: (
                        <NotificationTable
                            i18n={i18n.strings}
                            list={notifications}
                            notificationForm={notificationForm}
                            removeRegister={removeRegister}
                            loading={isModalLoading}
                        />
                    ),
                    notificationForm: (
                        <NotificationForm
                            i18n={i18n.strings}
                            cancel={() => setView('notificationList')}
                            data={Object.assign({}, data)}
                            calendars={calendars}
                            geofences={geofences}
                            alarmTypes={alarmTypes}
                            allNotificator={allNotificator}
                            allNotificationTypes={allNotificationTypes}
                            edit={Object.keys(data).length}
                            newRegister={newRegister}
                            updateRegister={updateRegister}
                            loading={isModalLoading}
                        />
                    ),
                }[view]
            }
        </>
    );
};

const mapping = ({store}) => ({
    auth: store.auth,
    alarmTypes: store.alarmTypes,
    allNotificationTypes: store.allNotificationTypes,
    allNotificators: store.allNotificators,
    i18n: store.i18n,
});

export default inject(mapping)(observer(NotificationContainer));
